import { Transaction } from "@/types/wallet";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface VolumeDataPoint {
  label: string;
  inboundCount: number;
  outboundCount: number;
  inboundTotal: number;
  outboundTotal: number;
}

export type VolumeAggregation = VolumeDataPoint[];

export interface PointsDataPoint {
  label: string;
  cp: number;
  gp: number;
  sp: number;
}

export type PointsAggregation = PointsDataPoint[];

export interface PointsSummary {
  totalCp: number;
  totalGp: number;
  totalSp: number;
  grandTotal: number;
}

export interface SettlementRateSummary {
  settledCount: number;
  pendingCount: number;
  failedCount: number;
  total: number;
  settledPercent: number;
}

export interface CommissionSummary {
  totalFeesPaid: number;
  totalCommissionEarned: number;
  totalVolume: number;
  commissionRatePercent: number;
  netRevenue: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getDateKey(
  dateStr: string,
  period: "daily" | "weekly" | "monthly"
): string {
  const d = new Date(dateStr);
  if (period === "daily") {
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  }
  if (period === "weekly") {
    // ISO week bucket: year + week number
    const jan1 = new Date(d.getFullYear(), 0, 1);
    const week = Math.ceil(
      ((d.getTime() - jan1.getTime()) / 86400000 + jan1.getDay() + 1) / 7
    );
    return `${d.getFullYear()}-W${String(week).padStart(2, "0")}`;
  }
  // monthly
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function getDayLabel(key: string): string {
  // key = YYYY-MM-DD → "Mon", "Tue", etc.
  const d = new Date(key + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short" });
}

function getWeekLabel(key: string): string {
  // key = YYYY-Www
  const parts = key.split("-W");
  return `W${parts[1]}`;
}

function getMonthLabel(key: string): string {
  // key = YYYY-MM
  const parts = key.split("-");
  const mm = parts[1] ?? "";
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return months[parseInt(mm, 10) - 1] ?? key;
}

function buildLabel(key: string, period: "daily" | "weekly" | "monthly"): string {
  if (period === "daily") return getDayLabel(key);
  if (period === "weekly") return getWeekLabel(key);
  return getMonthLabel(key);
}

// ── aggregateVolume ────────────────────────────────────────────────────────────

export function aggregateVolume(
  transactions: Transaction[],
  period: "daily" | "weekly" | "monthly"
): VolumeAggregation {
  const map = new Map<string, VolumeDataPoint>();

  for (const tx of transactions) {
    const key = getDateKey(tx.created_at, period);
    if (!map.has(key)) {
      map.set(key, {
        label: buildLabel(key, period),
        inboundCount: 0,
        outboundCount: 0,
        inboundTotal: 0,
        outboundTotal: 0,
      });
    }
    const point = map.get(key)!;
    const amount = parseFloat(tx.raw_amount ?? tx.total_amount ?? "0") || 0;
    if (tx.direction === "Inbound") {
      point.inboundCount += 1;
      point.inboundTotal += amount;
    } else {
      point.outboundCount += 1;
      point.outboundTotal += amount;
    }
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, v]) => v);
}

// ── aggregatePoints ────────────────────────────────────────────────────────────

const CP_TYPES = ["CpEarn", "CpExchange", "CpTransfer"];
const GP_TYPES = ["GpEarn", "GpExchange", "GpTransfer"];
const SP_TYPES = ["SpEarn", "SpExchange", "SpTransfer"];

export function aggregatePoints(
  transactions: Transaction[],
  period: "weekly" | "monthly"
): PointsAggregation {
  const map = new Map<string, PointsDataPoint>();

  for (const tx of transactions) {
    const txType = tx.transaction_type ?? "";
    const isCp = CP_TYPES.some((t) => txType.startsWith(t));
    const isGp = GP_TYPES.some((t) => txType.startsWith(t));
    const isSp = SP_TYPES.some((t) => txType.startsWith(t));
    if (!isCp && !isGp && !isSp) continue;

    const key = getDateKey(tx.created_at, period);
    if (!map.has(key)) {
      map.set(key, { label: buildLabel(key, period), cp: 0, gp: 0, sp: 0 });
    }
    const point = map.get(key)!;
    const amount = parseFloat(tx.raw_amount ?? tx.total_amount ?? "0") || 0;
    if (isCp) point.cp += amount;
    else if (isGp) point.gp += amount;
    else point.sp += amount;
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, v]) => v);
}

// ── computeSettlementRate ─────────────────────────────────────────────────────

export function computeSettlementRate(
  transactions: Transaction[]
): SettlementRateSummary {
  let settledCount = 0;
  let pendingCount = 0;
  let failedCount = 0;

  for (const tx of transactions) {
    if (tx.settled === true) {
      settledCount += 1;
    } else if (tx.status === "failed") {
      failedCount += 1;
    } else {
      pendingCount += 1;
    }
  }

  const total = transactions.length;
  const settledPercent =
    total > 0
      ? Math.round((settledCount / total) * 1000) / 10 // 1 decimal place
      : 0;

  return { settledCount, pendingCount, failedCount, total, settledPercent };
}

// ── computeCommissionSummary ──────────────────────────────────────────────────

export function computeCommissionSummary(
  transactions: Transaction[]
): CommissionSummary {
  let totalFeesPaid = 0;
  let totalCommissionEarned = 0;
  let totalVolume = 0;

  for (const tx of transactions) {
    totalFeesPaid +=
      parseFloat(tx.calculated_fee_amount ?? "0") || 0;
    totalCommissionEarned +=
      parseFloat(tx.calculated_commission_amount ?? "0") || 0;
    totalVolume += parseFloat(tx.raw_amount ?? tx.total_amount ?? "0") || 0;
  }

  const commissionRatePercent =
    totalVolume > 0 ? (totalCommissionEarned / totalVolume) * 100 : 0;
  const netRevenue = totalCommissionEarned - totalFeesPaid;

  return {
    totalFeesPaid,
    totalCommissionEarned,
    totalVolume,
    commissionRatePercent,
    netRevenue,
  };
}
